import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import recallsService from '../../../../../services/recalls.service';
import { useForm, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import Loading from '../../../../common/Loading';
const formatTime = (timeString) => {
    if (!timeString) return '—';
    return timeString.substring(0, 5);
};

const WorkingBadge = ({ isWorking }) => (
    <span className={`recalls-status-badge ${isWorking ? 'recalls-status-badge--active' : 'recalls-status-badge--inactive'}`}>
        {isWorking ? 'Working' : 'Off'}
    </span>
);

function RecallTimings() {
    const { clinicId } = useParams();
    const [recallTimings, setRecallTimings] = useState([]);
    const [editing, setEditing] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getRecallTimings();
    }, [clinicId]);

    const getRecallTimings = () => {
        setLoading(true);
        recallsService
            .getClinicTimings(clinicId)
            .then((res) => {
                setRecallTimings(res.data);
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                toast.error('Failed to load recall timings');
                setLoading(false);
            });
    };

    const handleUpdateTiming = (newTiming) => {
        setRecallTimings(newTiming);
        setEditing(false);
    };

    const workingDays = recallTimings.filter((t) => t.isWorking).length;

    if (loading) {
        return <Loading />;
    }

    return (
        <div className="notifications-panel">
            <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                <span className="d-flex align-items-center gap-2">
                    <i className="bi bi-clock-history"></i>
                    Recall Timings
                    {recallTimings.length > 0 && (
                        <span className="text-muted small fw-normal">
                            ({workingDays} working / {recallTimings.length} days)
                        </span>
                    )}
                </span>
                {recallTimings.length > 0 && (
                    <button
                        type="button"
                        className={`btn btn-sm ${editing ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                        onClick={() => setEditing(!editing)}
                    >
                        <i className={`bi ${editing ? 'bi-x' : 'bi-pencil-square'} me-1`}></i>
                        {editing ? 'Cancel' : 'Edit'}
                    </button>
                )}
            </div>

            <div className="notifications-panel__body">
                {recallTimings.length > 0 ? (
                    editing ? (
                        <RecallTimingForm timings={recallTimings} updateTiming={handleUpdateTiming} setEditing={setEditing} />
                    ) : (
                        <>
                            <div className="recalls-toolbar">
                                <div>
                                    <strong>{workingDays}</strong> working · <strong>{recallTimings.length - workingDays}</strong> off
                                </div>
                            </div>
                            <RecallTimingView timings={recallTimings} />
                        </>
                    )
                ) : (
                    <div className="recalls-empty">
                        <i className="bi bi-clock"></i>
                        No recall timings configured.
                    </div>
                )}
            </div>
        </div>
    );
}

const RecallTimingView = ({ timings }) => (
    <>
        <div className="table-responsive">
            <table className="table table-bordered table-hover align-middle mb-0 process-queue-table recalls-table">
                <thead>
                    <tr>
                        <th>
                            <i className="bi bi-calendar3"></i> Day
                        </th>
                        <th>
                            <i className="bi bi-toggle-on"></i> Status
                        </th>
                        <th>
                            <i className="bi bi-play-circle"></i> Start Time
                        </th>
                        <th>
                            <i className="bi bi-stop-circle"></i> End Time
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {timings.map((timing) => (
                        <tr
                            key={timing.id}
                            className={`recalls-timing-row ${!timing.isWorking ? 'recalls-timing-row--off' : ''}`}
                        >
                            <td>
                                <span className="recalls-day">{timing.day}</span>
                            </td>
                            <td>
                                <WorkingBadge isWorking={timing.isWorking} />
                            </td>
                            <td>
                                {timing.isWorking ? (
                                    <span className="recalls-time">
                                        <i className="bi bi-clock"></i>
                                        {formatTime(timing.start)}
                                    </span>
                                ) : (
                                    <span className="text-muted">—</span>
                                )}
                            </td>
                            <td>
                                {timing.isWorking ? (
                                    <span className="recalls-time">
                                        <i className="bi bi-clock"></i>
                                        {formatTime(timing.end)}
                                    </span>
                                ) : (
                                    <span className="text-muted">—</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>

        <div className="notifications-alert-banner mt-3">
            <i className="bi bi-info-circle"></i>
            <span>Recall timings are based on the clinic&apos;s timezone.</span>
        </div>
    </>
);

const RecallTimingForm = ({ timings, updateTiming, setEditing }) => {
    const { control, handleSubmit, formState: { isSubmitting }, setValue } = useForm({
        defaultValues: {
            timings: timings.map((timing) => ({
                id: timing.id,
                clinicId: timing.clinicId,
                day: timing.day,
                isWorking: timing.isWorking,
                start: timing.start ? timing.start.substring(0, 5) : '10:00',
                end: timing.end ? timing.end.substring(0, 5) : '16:00',
            })),
        },
    });

    const [workingStates, setWorkingStates] = useState(
        timings.reduce((acc, timing, index) => {
            acc[index] = timing.isWorking;
            return acc;
        }, {})
    );

    const handleWorkingToggle = (index, isChecked) => {
        setWorkingStates((prev) => ({ ...prev, [index]: isChecked }));
        setValue(`timings[${index}].isWorking`, isChecked);

        if (!isChecked) {
            setValue(`timings[${index}].start`, '10:00');
            setValue(`timings[${index}].end`, '16:00');
        }
    };

    const onSubmit = async (data) => {
        try {
            const newData = data.timings.map((item) => ({
                id: item.id,
                clinicId: item.clinicId,
                day: item.day,
                isWorking: item.isWorking,
                start: item.isWorking ? `${item.start}:00` : null,
                end: item.isWorking ? `${item.end}:00` : null,
            }));

            const response = await recallsService.saveClinicTimings(timings[0].clinicId, newData);
            updateTiming(response.data);
            toast.success('Recall timings updated successfully!');
        } catch (err) {
            toast.error('Error updating recall timings. Please try again.');
            console.log(err);
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <div className="table-responsive">
                <table className="table table-bordered table-hover align-middle mb-0 process-queue-table recalls-table">
                    <thead>
                        <tr>
                            <th>
                                <i className="bi bi-calendar3"></i> Day
                            </th>
                            <th>
                                <i className="bi bi-toggle-on"></i> Working Day
                            </th>
                            <th>
                                <i className="bi bi-play-circle"></i> Start Time
                            </th>
                            <th>
                                <i className="bi bi-stop-circle"></i> End Time
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {timings.map((timing, index) => {
                            const isWorking = workingStates[index];

                            return (
                                <tr key={timing.id} className={`recalls-timing-row ${!isWorking ? 'recalls-timing-row--off' : ''}`}>
                                    <td>
                                        <span className="recalls-day">{timing.day}</span>
                                    </td>
                                    <td>
                                        <div className="form-check form-switch mb-0">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`isWorking-${timing.day}`}
                                                checked={isWorking}
                                                onChange={(e) => handleWorkingToggle(index, e.target.checked)}
                                            />
                                            <label className="form-check-label small" htmlFor={`isWorking-${timing.day}`}>
                                                {isWorking ? 'Yes' : 'No'}
                                            </label>
                                        </div>
                                    </td>
                                    <td>
                                        <Controller
                                            name={`timings[${index}].start`}
                                            control={control}
                                            defaultValue={timing.start ? timing.start.substring(0, 5) : '10:00'}
                                            render={({ field }) => (
                                                <input
                                                    type="time"
                                                    className="form-control form-control-sm"
                                                    {...field}
                                                    disabled={!isWorking}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    step="300"
                                                />
                                            )}
                                        />
                                    </td>
                                    <td>
                                        <Controller
                                            name={`timings[${index}].end`}
                                            control={control}
                                            defaultValue={timing.end ? timing.end.substring(0, 5) : '16:00'}
                                            render={({ field }) => (
                                                <input
                                                    type="time"
                                                    className="form-control form-control-sm"
                                                    {...field}
                                                    disabled={!isWorking}
                                                    onChange={(e) => field.onChange(e.target.value)}
                                                    step="300"
                                                />
                                            )}
                                        />
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className="recalls-form-actions">
                <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setEditing(false)}>
                    Cancel
                </button>
                <button className="btn btn-success btn-sm" type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                            Saving…
                        </>
                    ) : (
                        <>
                            <i className="bi bi-floppy me-1"></i>
                            Save Changes
                        </>
                    )}
                </button>
            </div>
        </form>
    );
};

export default RecallTimings;
